'use client';

import React, { useEffect, useRef } from 'react';
import type { VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn/cn';
import { dialogVariants } from './Dialog.variants';

export type DialogProps = React.HTMLAttributes<HTMLDialogElement> &
  VariantProps<typeof dialogVariants> & {
    isOpen: boolean;
    /** Callback fired on dialog close */
    onClose: () => void;
  };

/**
 * A dialog implementing the visual layout of the empiriecom design system
 */
const Dialog = ({
  children,
  isOpen = false,
  onClose,
  variant = 'default',
  className,
  ...props
}: DialogProps) => {
  const ref = useRef<HTMLDialogElement>(null);
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  useEffect(() => {
    if (isOpen) {
      ref.current?.showModal();
    } else {
      ref.current?.close();
    }
  }, [isOpen]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <dialog
      className={cn(
        dialogVariants({ variant }),
        { 'flex md:m-auto md:h-fit md:min-h-0 md:min-w-0': isOpen },
        className,
      )}
      ref={ref}
      onCancel={onClose}
      // Handle not closing the dialog if its size changes and the mouse is on the backdrop
      onMouseDown={(e: React.MouseEvent<HTMLDialogElement>) => {
        mouseDownTargetRef.current = e.target;
      }}
      onClick={(event: React.MouseEvent<HTMLDialogElement>) => {
        if (event.target === ref.current) {
          const originalTarget = mouseDownTargetRef.current;
          if (originalTarget === ref.current) {
            onClose();
          }
        }
        mouseDownTargetRef.current = null;
      }}
      {...props}
    >
      {isOpen && children}
    </dialog>
  );
};

Dialog.displayName = 'Dialog';

export { Dialog };
